import React, { useState, useMemo, useEffect } from "react";
import ReportLayout, {
  DateRangePicker,
  StatusBadge,
  ProgressBar,
} from "./components/ReportLayout";
import {
  Utensils,
  TrendingUp,
  TrendingDown,
  ShoppingBag,
  DollarSign,
  BarChart3,
  Leaf,
  Drumstick,
  Building2,
  ChevronDown,
} from "lucide-react";

import useItemWiseSales from "@/hooks/useItemWiseSales";
import { categoryService } from "@/services";
import { useAuth } from "@/context/AuthContext";
import { P } from "@/config/permissionCodes";

export default function ItemWiseSalesReport() {
  const { hasPermission } = useAuth();

  if (!hasPermission(P.REPORTS.SALES.SCREEN)) {
    return (
      <div className="p-6 text-center">
        <p className="text-red-500 font-semibold">
          You do not have permission to access Item-wise Sales Report
        </p>
      </div>
    );
  }
  const exportToCsv = () => {
    if (!data?.length) return;

    const headers = columns.map((c) => `"${c.label}"`).join(",");
    const rows = data.map((r) =>
      columns.map((c) => `"${r[c.key] ?? ""}"`).join(",")
    );

    const blob = new Blob([headers, "\n", rows.join("\n")], {
      type: "text/csv;charset=utf-8;",
    });

    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "item-wise-sales-report.csv";
    link.click();
  };

  const formatDate = (d) =>
    d instanceof Date ? d.toISOString().split("T")[0] : d;

  // const getPresetDates = (preset) => {
  //   const today = new Date();

  //   switch (preset) {
  //     case "today": {
  //       const d = formatDate(today);
  //       return { start: d, end: d };
  //     }

  //     case "yesterday": {
  //       const y = new Date(today);
  //       y.setDate(y.getDate() - 1);
  //       const d = formatDate(y);
  //       return { start: d, end: d };
  //     }

  //     case "thisWeek": {
  //       const start = new Date(today);
  //       start.setDate(today.getDate() - today.getDay());
  //       return { start: formatDate(start), end: formatDate(today) };
  //     }

  //     case "thisMonth": {
  //       const start = new Date(today.getFullYear(), today.getMonth(), 1);
  //       return { start: formatDate(start), end: formatDate(today) };
  //     }

  //     case "lastMonth": {
  //       const start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
  //       const end = new Date(today.getFullYear(), today.getMonth(), 0);
  //       return { start: formatDate(start), end: formatDate(end) };
  //     }

  //     default:
  //       return null;
  //   }
  // };
  const today = formatDate(new Date());
  const [isCustomDate, setIsCustomDate] = useState(false);

  const [selectedPreset, setSelectedPreset] = useState("today");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [category, setCategory] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [vegFilter, setVegFilter] = useState("all");

  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const applyPreset = (preset, page = 1, size = pageSize) => {
  setCurrentPage(page);

  setFilters((prev) => ({
    ...prev,
    period: preset,
    startDate: null,
    endDate: null,
    category: selectedCategory || undefined,
    isVeg:
      vegFilter === "veg" ? true : vegFilter === "nonveg" ? false : undefined,
    offset: (page - 1) * size,
    limit: size,
    sortColumn,
    sortDirection,
  }));
};

const applyCustomDate = (page = 1, size = pageSize) => {
  setCurrentPage(page);

  setFilters((prev) => ({
    ...prev,
    period: null,
    startDate,
    endDate,
    category: selectedCategory || undefined,
    isVeg:
      vegFilter === "veg" ? true : vegFilter === "nonveg" ? false : undefined,
    offset: (page - 1) * size,
    limit: size,
    sortColumn,
    sortDirection,
  }));
};
useEffect(() => {
  applyPreset("today");
}, []);


  // useEffect(() => {
  //   const presetDates = getPresetDates(selectedPreset);
  //   if (presetDates) {
  //     setStartDate(presetDates.start);
  //     setEndDate(presetDates.end);
  //   }
  // }, [selectedPreset]);

  useEffect(() => {
    const loadCategories = async () => {
      try {
        setCategoriesLoading(true);
        const res = await categoryService.getAll({ isActive: true });
        setCategories(res || []);
      } catch (err) {
        console.error("Failed to load categories", err);
        setCategories([]);
      } finally {
        setCategoriesLoading(false);
      }
    };

    loadCategories();
  }, []);

  const [sortColumn, setSortColumn] = useState("revenue");
  const [sortDirection, setSortDirection] = useState("desc");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [searchQuery, setSearchQuery] = useState("");

  const apiFilters = useMemo(
    () => ({
      startDate,
      endDate,
      category: selectedCategory || undefined,
      isVeg:
        vegFilter === "veg" ? true : vegFilter === "nonveg" ? false : undefined,
      offset: (currentPage - 1) * pageSize,
      limit: pageSize,
      // sortColumn,
      // sortDirection,
    }),
    [
      startDate,
      endDate,
      selectedCategory,
      vegFilter,
      currentPage,
      pageSize,
      // sortColumn,
      // sortDirection,
    ]
  );

  const {
    data = [],
    summary = {},
    branchSummary = [],
    totalItems = 0,
    loading,
    setFilters,
    refresh,
  } = useItemWiseSales(apiFilters);

  const [selectedBranchId, setSelectedBranchId] = useState(null);

  const [cachedBranchOptions, setCachedBranchOptions] = useState([]);
  useEffect(() => {
    if (branchSummary && branchSummary.length > 1 && !selectedBranchId) {
      setCachedBranchOptions(branchSummary.map(b => ({ id: b.branchId, name: b.branchName || b.branchId })));
    }
  }, [branchSummary, selectedBranchId]);
  const branchOptions = cachedBranchOptions;

  const normalizedData = useMemo(() => {
    let rows = [...(data || [])];

    if (selectedCategory) {
      rows = rows.filter(
        (r) => String(r.categoryName) === String(selectedCategory)
      );
    }

    if (vegFilter === "veg") {
      rows = rows.filter((r) => r.isVeg === true);
    } else if (vegFilter === "nonveg") {
      rows = rows.filter((r) => r.isVeg === false);
    }

    return rows;
  }, [data, selectedCategory, vegFilter]);

  const topPerformers = useMemo(
    () => normalizedData.slice(0, 3),
    [normalizedData]
  );
  const bottomPerformers = useMemo(
    () => normalizedData.slice(-3).reverse(),
    [normalizedData]
  );

  const summaryCards = [
    {
      title: "Total Items Sold",
      value: Number(summary?.totalItemsSold ?? 0).toLocaleString(),
      subtitle: "Units sold in period",
      icon: ShoppingBag,
    },
    {
      title: "Total Revenue",
      value: `₹${Number(summary?.totalRevenue ?? 0).toLocaleString()}`,
      subtitle: "Gross revenue",
      icon: DollarSign,
    },
    {
      title: "Unique Items",
      value: Number(summary?.uniqueItemsSold ?? summary?.uniqueItems ?? 0).toLocaleString(),
      subtitle: "Different items sold",
      icon: Utensils,
    },
    {
      title: "Avg Item Price",
      value: `₹${Number(summary?.avgItemPrice ?? 0).toFixed(0)}`,
      subtitle: "Average selling price",
      icon: BarChart3,
    },
  ];

  const columns = [
    {
      key: "rank",
      label: "#",
      sortable: true,
      width: "60px",
      render: (v = 0) => (
        <span
          className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
            v <= 3 ? "bg-amber-100 text-amber-700" : "bg-gray-100 text-gray-600"
          }`}
        >
          {v}
        </span>
      ),
    },
    {
      key: "itemCode",
      label: "Code",
      sortable: true,
      render: (v) => (
        <span className="font-mono text-xs text-gray-500">{v}</span>
      ),
    },
    {
      key: "itemName",
      label: "Item Name",
      sortable: true,
      render: (value, row) => (
        <div className="flex items-center gap-2">
          <span className="font-medium">{value}</span>
          {row.isVeg ? (
            <span className="w-4 h-4 border-2 border-green-600 rounded-sm flex items-center justify-center">
              <span className="w-2 h-2 bg-green-600 rounded-full" />
            </span>
          ) : (
            <span className="w-4 h-4 border-2 border-red-600 rounded-sm flex items-center justify-center">
              <span className="w-2 h-2 bg-red-600 rounded-full" />
            </span>
          )}
        </div>
      ),
    },
    {
      key: "categoryName",
      label: "Category",
      sortable: true,
      render: (v) => <StatusBadge status={v} type="info" />,
    },
    {
      key: "quantitySold",
      label: "Qty Sold",
      sortable: true,
      align: "right",
      render: (v) => v.toLocaleString(),
    },
    {
      key: "grossRevenue",
      label: "Revenue",
      sortable: true,
      align: "right",
      render: (v) => `₹${v.toLocaleString()}`,
    },
    {
      key: "avgSellingPrice",
      label: "Avg Price",
      sortable: true,
      align: "right",
      render: (v) => `₹${v.toFixed(0)}`,
    },
    {
      key: "contribution",
      sortable: true,
      label: "Contribution",
      align: "right",
      render: (v) => <ProgressBar
      value={Number(v)}
      max={100}
      color="teal"
      label={`${Number(v).toFixed(2)}%`}
    />
    }
  ];

  const isApplyDisabled = !startDate && !endDate;

  const filters = (
    <div className="space-y-4">
      <DateRangePicker
        startDate={startDate}
        endDate={endDate}
        selectedPreset={selectedPreset}
        onPresetSelect={(preset) => {
        setSelectedPreset(preset);
        setIsCustomDate(false);
        setCurrentPage(1);
        applyPreset(preset);
        }}
        onStartDateChange={(d) => {
        setStartDate(formatDate(d));
        setSelectedPreset(null);
        setIsCustomDate(true);
        }}
        onEndDateChange={(d) => {
        setEndDate(formatDate(d));
        setSelectedPreset(null);
        setIsCustomDate(true);
        }}
      />

      <div className="flex flex-wrap items-center gap-4 pt-4 border-t">
        <div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-gray-600">
              Category
            </label>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 min-w-[180px]"
            >
              <option value="">All Categories</option>

              {categories.map((cat) => (
                <option key={cat.id} value={cat.name}>
                  {cat.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label className="text-xs text-gray-600">Type</label>
          <div className="flex items-center gap-1 bg-gray-100 p-1 rounded-lg">
            <button
              onClick={() => setVegFilter("all")}
              className={`h-9 px-3 rounded-md text-xs font-medium flex items-center justify-center
      ${
        vegFilter === "all"
          ? "bg-white shadow text-gray-900"
          : "text-gray-600 hover:text-gray-900"
      }`}
            >
              All
            </button>

            <button
              onClick={() => setVegFilter("veg")}
              className={`h-9 px-3 rounded-md text-xs font-medium flex items-center gap-1 justify-center
      ${
        vegFilter === "veg"
          ? "bg-white shadow text-green-700"
          : "text-gray-600 hover:text-gray-900"
      }`}
            >
              <Leaf size={14} /> Veg
            </button>

            <button
              onClick={() => setVegFilter("nonveg")}
              className={`h-9 px-3 rounded-md text-xs font-medium flex items-center gap-1 justify-center
      ${
        vegFilter === "nonveg"
          ? "bg-white shadow text-red-700"
          : "text-gray-600 hover:text-gray-900"
      }`}
            >
              <Drumstick size={14} /> Non-Veg
            </button>
          </div>
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-transparent">Apply</label>

          <button
            disabled={!isCustomDate}
              onClick={() => applyCustomDate()}
              className={`h-9 px-5 rounded-lg text-sm font-medium ${
              isCustomDate
            ? "bg-teal-600 text-white hover:bg-teal-700"
            : "bg-gray-300 text-gray-500 cursor-not-allowed"
          }`}
          >
            Apply Filters
          </button>
        </div>

        {branchOptions.length > 0 && (
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-gray-600">Branch</label>
            <div className="relative">
              <Building2 size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
              <select
                value={selectedBranchId ?? ''}
                onChange={(e) => {
                  const val = e.target.value || null;
                  setSelectedBranchId(val);
                  setCurrentPage(1);
                  setFilters(prev => ({ ...prev, branchId: val || undefined, offset: 0 }));
                }}
                className="pl-9 pr-8 h-9 text-sm border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-teal-500 focus:border-teal-500 appearance-none cursor-pointer"
              >
                <option value="">All Branches</option>
                {branchOptions.map(b => (
                  <option key={b.id} value={b.id}>{b.name}</option>
                ))}
              </select>
              <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            </div>
          </div>
        )}
      </div>
    </div>
  );
  
  //SORTING
  const sortedData = useMemo(() => {
  if (!normalizedData || !sortColumn) return normalizedData;

  const sorted = [...normalizedData];

  sorted.sort((a, b) => {
    let aVal = a[sortColumn];
    let bVal = b[sortColumn];

    if (typeof aVal === "number" && typeof bVal === "number") {
      return sortDirection === "asc"
        ? aVal - bVal
        : bVal - aVal;
    }

    return sortDirection === "asc"
      ? String(aVal ?? "").localeCompare(String(bVal ?? ""))
      : String(bVal ?? "").localeCompare(String(aVal ?? ""));
  });

  return sorted;
}, [normalizedData, sortColumn, sortDirection]);

// CLIENT-SIDE FILTERING - Search across all columns
const filteredData = useMemo(() => {
  if (!searchQuery.trim() || !sortedData) return sortedData;
  const query = searchQuery.toLowerCase().trim();
  return sortedData.filter(row => {
    // Search in text fields (convert to string first to handle numbers)
    if (String(row.itemName || '').toLowerCase().includes(query)) return true;
    if (String(row.itemCode || '').toLowerCase().includes(query)) return true;
    if (String(row.categoryName || '').toLowerCase().includes(query)) return true;
    // Search in numeric fields
    const numericFields = ['rank', 'quantitySold', 'grossRevenue', 'avgSellingPrice', 'contribution'];
    for (const field of numericFields) {
      if (row[field] !== undefined && row[field] !== null) {
        if (String(row[field]).includes(query) || Number(row[field]).toLocaleString().includes(query)) return true;
      }
    }
    return false;
  });
}, [sortedData, searchQuery]);

  return (
    <ReportLayout
      title="Item-wise Sales Report"
      description="Analyze best & worst selling items for menu optimization"
      icon={Utensils}
      exportPermission={P.REPORTS.SALES.ITEMWISE_EXPORT}
      color="teal"
      summaryCards={summaryCards}
      columns={columns}
      data={filteredData}
      filters={filters}
      loading={loading}
      totalItems={totalItems}
      searchPlaceholder="Search by item name, code, category..."
      sortColumn={sortColumn}
      sortDirection={sortDirection}
      onSort={(column) => {
      let nextDirection;

        if (sortColumn === column) {
          nextDirection = sortDirection === "asc" ? "desc" : "asc";
       } else {
        nextDirection = "desc";
      }

      setSortColumn(column);
      setSortDirection(nextDirection);
    }}

      onRefresh={() => {
        setCurrentPage(1);
        refresh();
      }}
      onExport={(format) => {
        if (format === "csv") exportToCsv();
      }}
      onSearch={setSearchQuery}
      pagination={{
        currentPage,
        totalPages: Math.ceil(totalItems / pageSize),
        totalItems,
        pageSize,
        onPageChange: setCurrentPage,
        onPageSizeChange: setPageSize,
      }}
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
              <TrendingUp size={16} className="text-green-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">Top Performers</h3>
              <p className="text-xs text-gray-500">Highest revenue items</p>
            </div>
          </div>

          <div className="space-y-3">
            {topPerformers.map((item, index) => (
              <div
                key={item.itemCode}
                className="flex items-center justify-between p-2 bg-gray-50 rounded-lg"
              >
                <div className="flex items-center gap-3">
                  <span className="w-6 h-6 bg-amber-100 text-amber-700 rounded-full flex items-center justify-center text-xs font-bold">
                    {index + 1}
                  </span>
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {item.itemName}
                    </p>
                    <p className="text-xs text-gray-500">
                      {item.quantitySold} sold
                    </p>
                  </div>
                </div>
                <span className="text-sm font-semibold text-green-600">
                  ₹{item.grossRevenue.toLocaleString()}
                </span>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center">
              <TrendingDown size={16} className="text-red-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">Needs Attention</h3>
              <p className="text-xs text-gray-500">Lowest revenue items</p>
            </div>
          </div>

          <div className="space-y-3">
            {bottomPerformers.map((item, index) => (
              <div
                key={item.itemCode}
                className="flex items-center justify-between p-2 bg-gray-50 rounded-lg"
              >
                <div className="flex items-center gap-3">
                  <span className="w-6 h-6 bg-gray-100 text-gray-600 rounded-full flex items-center justify-center text-xs font-bold">
                    {totalItems - index}
                  </span>
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {item.itemName}
                    </p>
                    <p className="text-xs text-gray-500">
                      {item.quantitySold} sold
                    </p>
                  </div>
                </div>
                <span className="text-sm font-semibold text-red-600">
                  ₹{item.grossRevenue.toLocaleString()}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </ReportLayout>
  );
}
